import { useCategorySearch } from '@shopware/composables'
import { getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'
import { useShopwareSwitchLocale } from '~/composables/useShopwareSwitchLocale'

export function useCategories() {
  const localePath = useLocalePath()
  const { advancedSearch } = useCategorySearch()
  const { currentShopwareLanguageId, defaultShopwareLanguageId } = useShopwareSwitchLocale()

  const categories = ref<Schemas['Category'][]>([])
  const categoriesTree = computed(() => nestCategories(mapCategories(categories.value)))

  async function fetchCategories() {
    try {
      const result = await advancedSearch({
        query: {
          filter: [
            {
              type: 'multi',
              operator: 'OR',
              queries: [
                {
                  type: 'equals',
                  field: 'level',
                  value: 2,
                },
                {
                  type: 'equals',
                  field: 'level',
                  value: 3,
                },
              ],
            },
            {
              type: 'multi',
              operator: 'AND',
              queries: [
                {
                  type: 'equals',
                  field: 'active',
                  value: true,
                },
              ],
            },
            {
              type: 'multi',
              operator: 'AND',
              queries: [
                {
                  type: 'not',
                  operator: 'OR',
                  queries: [
                    {
                      type: 'equals',
                      field: 'id',
                      value: '0195b8e86b6d727a89c004210531a209',
                    },
                    {
                      type: 'equals',
                      field: 'id',
                      value: '0195b8e86b6d727a89c0042105c0413b',
                    },
                    {
                      type: 'equals',
                      field: 'id',
                      value: '0195b8e86b6c721d999bb1d2f356ad35',
                    },
                    {
                      type: 'contains',
                      field: 'path',
                      value: '0195b8e86b6d727a89c004210531a209',
                    },
                    {
                      type: 'contains',
                      field: 'path',
                      value: '0195b8e86b6d727a89c0042105c0413b',
                    },
                    {
                      type: 'contains',
                      field: 'path',
                      value: '0195b8e86b6c721d999bb1d2f356ad35',
                    },
                  ],
                },
              ],
            },
          ],
          associations: {
            seoUrls: {},
          },
        },

      })

      categories.value = result
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  type MappedCategory = {
    page: {
      id: string
      parentId: string | null
      title: string
      url: string
      external?: boolean // Optional external flag
    }
    children: MappedCategory[] // Recursive type for nested structure
  }

  function mapCategories(categories: Schemas['Category'][]): MappedCategory[] {
    return categories.map((category) => {
      const { externalLink } = category

      if (externalLink) {
        return {
          page: {
            id: category.id,
            parentId: category.parentId ?? null,
            title: getTranslatedProperty(category, 'name'),
            url: externalLink,
            external: true,
          },
          children: [],
        }
      }

      // Get current locale SEO URL from extensions, fallback to default locale if not available
      const categoryRoute = category.extensions?.novuSeoUrls?.[currentShopwareLanguageId.value] ?? category.extensions?.novuSeoUrls?.[defaultShopwareLanguageId]
      const cleanedPath = categoryRoute?.split('/')?.filter(Boolean) ?? []

      return {
        page: {
          id: category.id,
          parentId: category.parentId ?? null,
          title: getTranslatedProperty(category, 'name'),
          url: localePath({
            name: 'products-all',
            params: { all: cleanedPath },
          }),
          external: false,
        },
        children: [],
      }
    })
  }

  function nestCategories(flatArray: MappedCategory[]): MappedCategory[] {
    const nodeMap: Record<string, MappedCategory> = {}
    flatArray.forEach((node) => {
      nodeMap[node.page.id] = {
        ...node,
        children: [],
      }
    })

    const tree: MappedCategory[] = []
    flatArray.forEach((node) => {
      const currentNode = nodeMap[node.page.id]
      if (!node.page.parentId || !nodeMap[node.page.parentId]) {
        tree.push(currentNode)
      } else {
        nodeMap[node.page.parentId]?.children.push(currentNode)
      }
    })

    return tree
  }

  return {
    categories,
    categoriesTree,
    fetchCategories,
  }
}
